import { useCallback, useEffect, useState } from 'react'

import { cacheRead, cacheWrite } from './sessionCache'

/**
 * One read, with its loading, error and retry states.
 *
 * ── WHY A HOOK AT ALL ───────────────────────────────────────────────────────
 *
 * Sprint 1.8's four admin screens each hand-roll `useEffect` plus a `cancelled`
 * flag (`src/pages/admin/ApprovalQueue.tsx`), which is the right amount of
 * machinery for four screens with one read each. Sprint 1.9 adds seven screens,
 * several with more than one read, one that polls with backoff and one that fans
 * out over a page of rows. Copying the pattern eleven more times is where the
 * cancelled-flag idiom stops being cheap and starts being eleven chances to
 * forget it.
 *
 * By default it is NOT a cache. There is no shared store, no stale-while-
 * revalidate and no deduplication: a component that unmounts forgets what it
 * read, which is what §4.5's "clear user-specific cached data on sign-out"
 * wants to be true by construction rather than by a cleanup routine somebody has
 * to remember to call.
 *
 * `cacheKey` is the one opt-in (Home, owner instruction 2026-10-01; see
 * `sessionCache.ts`): with a key that has an answer, the hook starts SETTLED on
 * that answer and runs the read again quietly — `isLoading` stays false — and
 * an equal answer keeps the same object, so nothing re-renders. A key names the
 * user and the read, and the cache is cleared on sign-out.
 *
 * ── HOW TO USE IT ───────────────────────────────────────────────────────────
 *
 * `load` must be stable — wrap it in `useCallback` with the ids it closes over:
 *
 *     const load = useCallback((signal) => getRecording(id, { signal }), [id])
 *     const recording = useResource(load)
 *
 * An unstable `load` re-runs the read on every render, which is a request loop
 * rather than a subtle bug, so it shows up immediately.
 */

export interface Resource<T> {
  data: T | null
  error: unknown
  /** True until the first attempt settles, and again on an explicit `reload`. */
  isLoading: boolean
  reload: () => void
}

export interface ResourceOptions {
  /** Opt in to the session cache under this key (user and read in it); `null` opts out. */
  cacheKey?: string | null
}

export function useResource<T>(load: (signal: AbortSignal) => Promise<T>, { cacheKey = null }: ResourceOptions = {}): Resource<T> {
  const [data, setData] = useState<T | null>(() => cacheRead<T>(cacheKey) ?? null)
  const [error, setError] = useState<unknown>(null)
  // Starts true so the effect never has to turn loading ON -- which is what
  // `react-hooks/set-state-in-effect` is pointing at, and is also better: the
  // render that would show "not loading, no data yet" never happens. A cached
  // answer starts it settled; the read below still runs.
  const [isLoading, setIsLoading] = useState(() => cacheRead<T>(cacheKey) === undefined)
  const [attempt, setAttempt] = useState(0)

  const reload = useCallback(() => setAttempt((n) => n + 1), [])

  useEffect(() => {
    const controller = new AbortController()
    let cancelled = false

    void (async () => {
      try {
        const value = await load(controller.signal)
        if (cancelled) return
        setData(cacheKey === null ? value : cacheWrite(cacheKey, value))
        setError(null)
      } catch (err) {
        // An abort is this component going away, not a failure to report.
        if (cancelled || controller.signal.aborted) return
        setError(err)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    })()

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [load, attempt, cacheKey])

  // A retry shows its own loading state. Done as a render-time adjustment rather
  // than in the effect, for the reason AppShell resets its drawer that way: an
  // effect runs a frame later, which is long enough to paint the previous error
  // underneath a request that has already started.
  const [lastAttempt, setLastAttempt] = useState(attempt)
  if (lastAttempt !== attempt) {
    setLastAttempt(attempt)
    setIsLoading(true)
  }

  /**
   * A CHANGED `load` IS A DIFFERENT READ, and the old answer is not its answer.
   *
   * ── THE BUG THIS EXISTS TO PREVENT ──────────────────────────────────────────
   *
   * Without this, a changed `load` re-runs the effect but leaves `data` and
   * `isLoading` exactly as they were. The hook then reports a **settled** result
   * that belongs to the previous input, and every caller renders it under the new
   * input's label.
   *
   * On Baseline that is not cosmetic. Selecting "eyes open" changes the heading
   * immediately and the numbers a request later, so for that interval the screen
   * shows the EC baseline's session count captioned *Eyes open* — a measurement
   * attributed to the wrong condition, which is the one thing §5.1 exists to
   * stop. EC and EO are separate baselines; an EC number under an EO label is a
   * false statement, not a slow one.
   *
   * It also made every `isLoading` branch downstream unreachable on a second
   * read, so Today's "Loading your result…" card never rendered: the list settles,
   * `load` changes to name the picked recording, and the hook says it is not
   * loading while it loads.
   *
   * Held in a WRAPPER OBJECT rather than as bare state, because `useState(fn)`
   * treats a function as a lazy initialiser and would call it, and `setState(fn)`
   * treats one as an updater. Both would be silent.
   */
  const [lastLoad, setLastLoad] = useState<{ fn: typeof load }>({ fn: load })
  if (lastLoad.fn !== load) {
    setLastLoad({ fn: load })
    // The NEW read's own cached answer, when it has one: never the old read's.
    const hit = cacheRead<T>(cacheKey)
    setData(hit ?? null)
    setError(null)
    setIsLoading(hit === undefined)
  }

  return { data, error, isLoading, reload }
}
